<template>
  <main
    ref="landingRef"
    class="relative overflow-hidden w-full spotlight-bg"
    :style="spotlightStyle"
  >
    <canvas
      ref="canvasRef"
      class="pointer-events-none fixed inset-0 h-full w-full z-0"
    />

    <div class="fixed inset-0 z-0 pointer-events-none">
      <div
        class="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-brand/10 blur-[140px] rounded-full animate-pulse"
      />
      <div
        class="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-orange-600/5 blur-[140px] rounded-full animate-pulse"
        :style="{ animationDuration: '12s' }"
      />
    </div>

    <section
      class="relative z-10 w-full px-6 sm:px-12 lg:px-20 pt-32 lg:pt-40 pb-12 hero"
    >
      <div class="flex flex-col items-center text-center gap-y-6 mb-12">
        <!-- <div
          class="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/3 border border-white/10 backdrop-blur-md group hover:border-brand/40 transition-colors cursor-default"
        >
          <div
            class="size-1.5 rounded-full bg-brand animate-pulse shadow-[0_0_8px_#ff5f00]"
          />
          <span
            class="text-[9px] font-bold tracking-[0.25em] text-white/60 uppercase"
          >
            Just In: Fresh Content Live
          </span>
        </div> -->

        <h1
          class="max-w-5xl text-white font-black tracking-[-0.03em] leading-[0.95] text-4xl sm:text-6xl md:text-7xl lg:text-8xl"
        >
          <span class="font-light tracking-[-0.01em] text-white/90 block mb-2"
            >Your Home of</span
          >
          <span class="text-brand">Authentic</span> local Content
        </h1>

        <p
          class="text-white/50 text-xs sm:text-sm font-medium tracking-[0.2em] uppercase max-w-xl leading-relaxed mt-2"
        >
          Premieres
          <span class="text-brand/40 mx-1">•</span> Movies
          <span class="text-brand/40 mx-1">•</span> Documentaries
          <span class="text-brand/40 mx-1">•</span> Short Films
          <span class="text-brand/40 mx-1">•</span> Series
        </p>
      </div>

      <div class="flex flex-wrap justify-center items-center gap-6">
        <PebbleAppLink
          to="/login"
          variant="primary"
          size="lg"
          class="text-xs font-bold tracking-[0.15em] uppercase"
          :leading-icon="Play"
        >
          Start Watching
        </PebbleAppLink>
      </div>
    </section>

    <section
      class="relative z-10 my-8 overflow-hidden select-none w-screen left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] perspective-container"
    >
      <div class="flex flex-col gap-y-6 pointer-events-auto">
        <div class="flex w-max gap-6 animate-marquee-left hover:pause-marquee">
          <div v-for="n in 2" :key="'track1-' + n" class="flex gap-6">
            <div
              v-for="(content, idx) in randomizedRowOne"
              :key="'row1-item-' + idx"
              class="relative w-44 sm:w-60 md:w-72 aspect-2/3 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/40 group shadow-2xl transition-all duration-500 volumetric-card"
            >
              <img
                :src="content.image"
                alt="African Cinematic Content"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div
                class="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />
            </div>
          </div>
        </div>

        <div class="flex w-max gap-6 animate-marquee-right hover:pause-marquee">
          <div v-for="n in 2" :key="'track2-' + n" class="flex gap-6">
            <div
              v-for="(content, idx) in randomizedRowTwo"
              :key="'row2-item-' + idx"
              class="relative w-44 sm:w-60 md:w-72 aspect-2/3 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/40 group shadow-2xl transition-all duration-500 volumetric-card"
            >
              <img
                :src="content.image"
                alt="African Cinematic Content"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div
                class="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { Play } from "lucide-vue-next";

// Import all cover asset links smoothly
import image1 from "~/assets/img/home/46fba2d6-c558-42f4-afa6-ced9c904e81f.jpg";
import image2 from "~/assets/img/home/5ed287db-2b4e-4871-a874-7b630d123776.jpg";
import image3 from "~/assets/img/home/A Sunday On The Moon design.jpg";
import image4 from "~/assets/img/home/BeigeRetroStyleMoviePoster.pn.jpeg";
import image5 from "~/assets/img/home/FUFU.jpg";
import image6 from "~/assets/img/home/IMG_8763.jpg";
import image7 from "~/assets/img/home/IMG_9321.jpg";
import image8 from "~/assets/img/home/PHOTO-2024-08-23-09-08-15.jpg";
import image9 from "~/assets/img/home/PHOTO-2024-12-04-06-46-31.jpg";
import image10 from "~/assets/img/home/PHOTO-2025-02-25-15-52-51.jpg";
import image11 from "~/assets/img/home/PHOTO-2025-03-04-17-55-52.jpg";
import image12 from "~/assets/img/home/PHOTO-2025-03-20-14-00-28.jpg";
import image13 from "~/assets/img/home/d4e061da-0a7e-40c3-a047-6e8766f19bb3.jpg";

// Three.js Canvas Composable Logic
const canvasRef = ref<HTMLCanvasElement | null>(null);
const { init, cleanup } = useLandingCanvas({
  particleColor: "#ff5f00",
  particleCount: 1600,
  particleSize: 0.022,
});

// Cursor Tracking States for Background Ambient Flashlight Spotlight
const mouseX = ref(0);
const mouseY = ref(0);

const handleSpotlightMove = (event: MouseEvent) => {
  mouseX.value = event.clientX;
  mouseY.value = event.clientY;
};

const spotlightStyle = computed(() => ({
  "--mouse-x": `${mouseX.value}px`,
  "--mouse-y": `${mouseY.value}px`,
}));

onMounted(() => {
  if (canvasRef.value) {
    init(canvasRef.value);
  }
  window.addEventListener("mousemove", handleSpotlightMove);
});

onUnmounted(() => {
  cleanup();
  window.removeEventListener("mousemove", handleSpotlightMove);
});

const marqueeRowOne = [
  { image: image13 },
  { image: image12 },
  { image: image11 },
  { image: image10 },
  { image: image9 },
  { image: image2 },
];

const marqueeRowTwo = [
  { image: image5 },
  { image: image6 },
  { image: image7 },
  { image: image8 },
  { image: image4 },
  { image: image3 },
];

// Helper formula to execute a high-performance Fisher-Yates shuffle protocol
const shuffleTrack = (array: any[]) => {
  const output = [...array];
  for (let i = output.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [output[i], output[j]] = [output[j], output[i]];
  }
  return output;
};

// Computed randomizers to hold structured sequence constant per session instance
const randomizedRowOne = computed(() => shuffleTrack(marqueeRowOne));
const randomizedRowTwo = computed(() => shuffleTrack(marqueeRowTwo));
</script>

<style scoped>
.spotlight-bg {
  /* Radial ambient glow setup tied directly to dynamic mouse coordinate states */
  background: radial-gradient(
    800px circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
    rgba(255, 95, 0, 0.07),
    transparent 80%
  );
}

.hero {
  animation: cinematicFade 1.2s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Base perspective environment configuration */
.perspective-container {
  perspective: 1200px;
}

.volumetric-card {
  transform-style: preserve-3d;
  will-change: transform, border-color, box-shadow;
}

/* Micro-tilt and glowing boundary highlight projection map rules when hovered */
.volumetric-card:hover {
  transform: scale(1.04) rotateY(-6deg) rotateX(4deg) translateY(-4px);
  border-color: rgba(255, 95, 0, 0.35);
  box-shadow:
    0 20px 40px -15px rgba(0, 0, 0, 0.7),
    0 0 30px -5px rgba(255, 95, 0, 0.15);
}

/* Pause scroll behavior softly during user hover investigation points */
.hover\:pause-marquee:hover {
  animation-play-state: paused;
}

.animate-marquee-left {
  animation: scrollLeft 95s linear infinite;
}

.animate-marquee-right {
  animation: scrollRight 85s linear infinite;
}

@keyframes scrollLeft {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes scrollRight {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes cinematicFade {
  from {
    opacity: 0;
    transform: translateY(25px) scale(0.99);
    filter: blur(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

h1 {
  font-family: "Inter Tight", "Inter", sans-serif;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.03);
}
</style>
